/**
 * Assessments Pages
 *
 * List and detail views for assessments.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';

import { RequestPanel, StatusBadge } from '../components/RequestPanel';
import { JsonViewer, Code } from '../components/JsonViewer';
import type { ApiResult, Assessment, AssessmentListResponse, ArtifactLinkListResponse } from '../lib/types';

export function Assessments() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<AssessmentListResponse> | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createResult, setCreateResult] = useState<ApiResult<Assessment> | null>(null);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('');
  const [assessmentType, setAssessmentType] = useState<'pre' | 'post'>('pre');
  const [metadata, setMetadata] = useState('{}');
  const [externalRefId, setExternalRefId] = useState('');

  const loadAssessments = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.listAssessments({ limit: 50 });
    setResult(res);
    if (res.response.data) {
      setAssessments(res.response.data.items);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    let parsedMetadata: Record<string, unknown> = {};
    try {
      parsedMetadata = JSON.parse(metadata);
    } catch {
      // Use empty object if invalid JSON
    }

    const res = await apiClient.createAssessment({
      name: name.trim() || 'Untitled Assessment',
      objective: objective.trim() || 'Test assessment',
      assessmentType,
      metadata: parsedMetadata,
      externalReferenceId: externalRefId || undefined,
    });

    setCreateResult(res);
    setCreating(false);

    if (res.response.data) {
      // Refresh list and navigate to new assessment
      await loadAssessments();
      navigate(`/console/assessments/${res.response.data.id}`);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
            Assessments
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
            Manage assessment records and linked artifacts
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-tertiary" onClick={loadAssessments} disabled={loading}>
            <iconify-icon icon="lucide:refresh-cw" width="16" height="16"></iconify-icon>
            Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? (
              <>
                <iconify-icon icon="lucide:x" width="16" height="16"></iconify-icon>
                Cancel
              </>
            ) : (
              <>
                <iconify-icon icon="lucide:plus" width="16" height="16"></iconify-icon>
                Create Assessment
              </>
            )}
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="card">
          <h2>Create Assessment</h2>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label className="label">Name</label>
              <input
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Assessment name (defaults to 'Untitled Assessment')"
              />
            </div>
            <div className="form-group">
              <label className="label">Objective</label>
              <textarea
                className="input"
                rows={2}
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                placeholder="Assessment objective (defaults to 'Test assessment')"
              />
            </div>
            <div className="form-group">
              <label className="label">Assessment Type</label>
              <select
                className="input"
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value as 'pre' | 'post')}
              >
                <option value="pre">Pre-training</option>
                <option value="post">Post-training</option>
              </select>
            </div>
            <div className="form-group">
              <label className="label">External Reference ID (optional)</label>
              <input
                type="text"
                className="input mono"
                value={externalRefId}
                onChange={(e) => setExternalRefId(e.target.value)}
                placeholder="e.g., call-12345"
              />
            </div>
            <div className="form-group">
              <label className="label">Metadata (JSON)</label>
              <textarea
                className="input mono"
                rows={4}
                value={metadata}
                onChange={(e) => setMetadata(e.target.value)}
                placeholder='{"key": "value"}'
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </button>
          </form>
          <RequestPanel result={createResult} loading={creating} />
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>External Ref</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((a) => (
              <tr
                key={a.id}
                className="clickable"
                onClick={() => navigate(`/console/assessments/${a.id}`)}
              >
                <td>
                  <Code>{a.id}</Code>
                </td>
                <td>
                  <StatusBadge status={a.status} />
                </td>
                <td className="mono" style={{ color: 'var(--slate-500)' }}>{a.externalReferenceId || '-'}</td>
                <td style={{ color: 'var(--slate-500)' }}>{new Date(a.createdAt).toLocaleString()}</td>
              </tr>
            ))}
            {assessments.length === 0 && !loading && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <iconify-icon icon="lucide:inbox" width="32" height="32" style={{ color: 'var(--slate-300)' }}></iconify-icon>
                    <span style={{ color: 'var(--slate-400)' }}>No assessments found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}

export function AssessmentDetail() {
  const { id } = useParams<{ id: string }>();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [links, setLinks] = useState<ArtifactLinkListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<Assessment> | null>(null);
  const [linksResult, setLinksResult] = useState<ApiResult<ArtifactLinkListResponse> | null>(null);

  const loadAssessment = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    const res = await apiClient.getAssessment(id);
    setResult(res);
    if (res.response.data) {
      setAssessment(res.response.data);
    }

    // Also load linked artifacts
    const linksRes = await apiClient.listArtifactLinks({ assessmentId: id });
    setLinksResult(linksRes);
    if (linksRes.response.data) {
      setLinks(linksRes.response.data);
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  return (
    <div>
      <Link
        to="/console/assessments"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--slate-500)',
          fontSize: 14,
          textDecoration: 'none',
          marginBottom: 16,
        }}
      >
        <iconify-icon icon="lucide:arrow-left" width="16" height="16"></iconify-icon>
        Back to Assessments
      </Link>

      <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 24 }}>
        Assessment Detail
      </h1>

      {assessment && (
        <>
          <div className="card">
            <h2>Summary</h2>
            <table className="table" style={{ maxWidth: 600 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: 180 }}>ID</td>
                  <td><Code>{assessment.id}</Code></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Status</td>
                  <td><StatusBadge status={assessment.status} /></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Company</td>
                  <td className="mono">{assessment.tenantId}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>External Reference</td>
                  <td className="mono">{assessment.externalReferenceId || '-'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Source System</td>
                  <td className="mono">{assessment.sourceSystem || '-'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created At</td>
                  <td>{new Date(assessment.createdAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created By</td>
                  <td className="mono">{assessment.createdBy}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Version</td>
                  <td>{assessment.version}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {assessment.metadata && Object.keys(assessment.metadata).length > 0 && (
            <div className="card">
              <h2>Metadata</h2>
              <JsonViewer data={assessment.metadata} />
            </div>
          )}

          <div className="card">
            <h2>Linked Artifacts</h2>
            {links && links.items.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Link Type</th>
                    <th>Artifact</th>
                    <th>Version</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {links.items.map((link) => (
                    <tr key={link.id}>
                      <td><Code>{link.linkType}</Code></td>
                      <td>
                        <Link to={`/console/artifacts/${link.artifactId}`}>
                          <Code>{link.artifactId.slice(0, 12)}...</Code>
                        </Link>
                      </td>
                      <td className="mono text-small">
                        {link.artifactVersionId.slice(0, 12)}...
                      </td>
                      <td className="text-muted">{new Date(link.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted">No artifacts linked to this assessment.</p>
            )}
          </div>

          <div className="card">
            <h2>Actions</h2>
            <div className="flex gap-2">
              <Link to={`/console/scores?assessmentId=${assessment.id}`} className="btn btn-primary">
                Generate Score
              </Link>
              <Link to={`/console/exports?assessmentId=${assessment.id}`} className="btn btn-secondary">
                Export Bundle
              </Link>
            </div>
          </div>
        </>
      )}

      <RequestPanel result={result} loading={loading} />
      {linksResult && <RequestPanel result={linksResult} />}
    </div>
  );
}
